import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Customer } from '../types';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';
import ReferralDiscountReasonModal from '../modals/ReferralDiscountReasonModal';
import {
  ReferralDiscountStatusBadge,
  ReferralKindBadge,
  ReferralStatusBadge,
  SummaryTile,
  formatReferralDate,
} from './referralUi';

const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

type Props = {
  customer: Customer;
  canGrant: boolean;
  onGranted: () => void;
};

export const hasReferralActivity = (customer: Customer) =>
  Boolean(customer.referred_by) || (customer.referrals?.length ?? 0) > 0 || (customer.referral_discounts?.length ?? 0) > 0;

export const InviteCodeLine: React.FC<{ customer: Customer; showCopyLink: boolean }> = ({ customer, showCopyLink }) => {
  if (!customer.invite_code) return null;

  const copyLink = async () => {
    if (!customer.invite_link) return;
    try {
      await navigator.clipboard.writeText(customer.invite_link);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  return (
    <p>
      <strong>Invite Code:</strong> {customer.invite_code}
      {showCopyLink && customer.invite_link && (
        <button type="button" onClick={copyLink} className="ml-2 text-sm text-blue-600 hover:text-blue-800 underline">
          Copy link
        </button>
      )}
    </p>
  );
};

export const ReferredByLine: React.FC<{ customer: Customer }> = ({ customer }) => (
  <p>
    <strong>Referred By:</strong>{' '}
    {customer.referred_by ? (
      <Link to={`/customers/${customer.referred_by.customer_id}`} className="text-blue-600 hover:underline">
        {customer.referred_by.name} ({customer.referred_by.code})
      </Link>
    ) : (
      'N/A'
    )}
  </p>
);

const CustomerReferrals: React.FC<Props> = ({ customer, canGrant, onGranted }) => {
  const [isGrantOpen, setIsGrantOpen] = useState(false);
  const [isGranting, setIsGranting] = useState(false);
  const referrals = customer.referrals ?? [];
  const discounts = customer.referral_discounts ?? [];
  const countByStatus = (status: string) => discounts.filter(discount => discount.status === status).length;

  const grant = async (reason: string) => {
    setIsGranting(true);
    try {
      const res = await fetchWithAuth(`/customers/${customer.id}/grant_referral_discount`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        toast.error(data?.errors?.[0] || 'Could not grant the referral discount.');
        return;
      }
      toast.success(data.message);
      setIsGrantOpen(false);
      onGranted();
    } catch {
      toast.error('Could not grant the referral discount.');
    } finally {
      setIsGranting(false);
    }
  };

  return (
    <section className="mb-6" aria-label="Referrals">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
        <h3 className="text-lg font-semibold">
          Referrals
          <span className="text-sm font-normal text-gray-500 ml-1">({referrals.length})</span>
        </h3>
        {canGrant && (
          <button
            onClick={() => setIsGrantOpen(true)}
            className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-auto"
          >
            + Grant Referral Discount
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <SummaryTile value={referrals.length} label="Friends referred" />
        <SummaryTile value={countByStatus('available')} label="Discounts available" valueClassName="text-green-700" />
        <SummaryTile value={countByStatus('reserved')} label="Discounts reserved" valueClassName="text-amber-700" />
        <SummaryTile value={countByStatus('redeemed')} label="Discounts redeemed" valueClassName="text-blue-700" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white shadow rounded-md overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead className="bg-gray-50">
              <tr>
                <th className={TH}>Friend Referred</th>
                <th className={TH}>Signed Up</th>
                <th className={TH}>First Order</th>
                <th className={TH}>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {referrals.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-gray-500">No friends referred yet.</td></tr>
              ) : (
                referrals.map(referral => (
                  <tr key={referral.id} data-testid={`customer-referral-${referral.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/customers/${referral.friend_id}`} className="text-blue-600 hover:underline">{referral.friend_name}</Link>
                      <div className="text-xs text-gray-500">{referral.friend_code}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatReferralDate(referral.signed_up_at) || <span className="text-gray-500">Not yet</span>}
                    </td>
                    <td className="px-4 py-3">
                      {referral.activating_order_id ? (
                        <Link to={`/orders/${referral.activating_order_id}`} className="text-blue-600 hover:underline">
                          {referral.activating_order_reference}
                        </Link>
                      ) : (
                        <span className="text-gray-500">No order yet</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><ReferralStatusBadge status={referral.status} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-white shadow rounded-md overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead className="bg-gray-50">
              <tr>
                <th className={TH}>Discount</th>
                <th className={TH}>Earned From</th>
                <th className={TH}>Status</th>
                <th className={TH}>Order</th>
                <th className={TH}>Saved</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {discounts.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-gray-500">No referral discounts yet.</td></tr>
              ) : (
                discounts.map(discount => (
                  <tr key={discount.id} data-testid={`customer-referral-discount-${discount.id}`}>
                    <td className="px-4 py-3">
                      <ReferralKindBadge kind={discount.kind} />
                      <div className="text-xs text-gray-500 mt-1">{Number(discount.percent)}% off delivery</div>
                    </td>
                    <td className="px-4 py-3">
                      {discount.earned_from}
                      <div className="text-xs text-gray-500">
                        {discount.grant_reason || formatReferralDate(discount.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3"><ReferralDiscountStatusBadge status={discount.status} /></td>
                    <td className="px-4 py-3">
                      {discount.order_id ? (
                        <Link to={`/orders/${discount.order_id}`} className="text-blue-600 hover:underline">{discount.order_reference}</Link>
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {discount.amount_saved !== null ? (
                        formatCurrency(Number(discount.amount_saved))
                      ) : discount.reserved_amount !== null ? (
                        <span className="text-gray-500">{formatCurrency(Number(discount.reserved_amount))} (pending)</span>
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        A friend becomes Active when their first order is paid, which earns one 20% off delivery discount. Discounts never expire, and one can be used per order.
      </p>

      <ReferralDiscountReasonModal
        isOpen={isGrantOpen}
        title="Grant referral discount"
        description={`Gives ${customer.full_name} one referral discount (20% off delivery) to use on an order of their choice. The reason and your name are saved with the discount.`}
        confirmLabel="Grant discount"
        savingLabel="Granting..."
        placeholder="e.g. Goodwill after a late delivery"
        isSaving={isGranting}
        onConfirm={grant}
        onCancel={() => setIsGrantOpen(false)}
      />
    </section>
  );
};

export default CustomerReferrals;
